import {Controller} from "@hotwired/stimulus"
import * as bootstrap from 'bootstrap'

export default class extends Controller {
  static targets = ["modal", "codeInput", "combinedCode", "totpInputs", "form", "submitBtn"]
  static values = {
    actionUrl: String,
    actionMethod: String,
    modalId: String
  }

  connect() {
    // Listen for custom events to show TOTP modal
    document.addEventListener('showTotpModal', this.handleShowTotpModal.bind(this))
    document.addEventListener('deleteSuccess', this.handleDeleteSuccess.bind(this))
  }

  disconnect() {
    this.cleanupModal()
    document.removeEventListener('showTotpModal', this.handleShowTotpModal.bind(this))
    document.removeEventListener('deleteSuccess', this.handleDeleteSuccess.bind(this))
  }

  handleShowTotpModal(event) {
    const {actionUrl, actionMethod, formData, rowToDelete, dataTable, redirectUrl} = event.detail
    console.log('showTotpModal event received:', event.detail)

    // Set the values for the modal
    this.actionUrlValue = actionUrl
    this.actionMethodValue = actionMethod
    this.formData = formData

    // Store additional data for delete action
    this.rowToDelete = rowToDelete
    this.dataTable = dataTable
    this.redirectUrl = redirectUrl

    // Show the modal
    this.showModal(event)
  }

  handleDeleteSuccess(event) {
    const {rowToDelete, dataTable, redirectUrl} = event.detail

    // Check if redirect is needed
    if (redirectUrl) {
      window.location.href = redirectUrl
      return
    }

    // Check if reload is needed (for episode delete)
    if (!rowToDelete && !dataTable) {
      window.location.reload()
      return
    }

    // Remove row from table
    if (rowToDelete) {
      rowToDelete.remove()
    }

    // Reload data table
    if (dataTable) {
      dataTable.ajax.reload()
    }
  }

  // Show TOTP verification modal
  showModal(event) {
    event.preventDefault()

    // Only get form data if this is a form submission
    if (this.hasFormTarget) {
      this.formData = this.getFormData()
    }

    if (this.hasModalTarget) {
      this.modal = new bootstrap.Modal(this.modalTarget, {
        backdrop: 'static',
        keyboard: false
      })

      this.modalTarget.addEventListener('shown.bs.modal', () => {
        this.initializeTOTPInputs()
        if (this.hasCodeInputTarget) {
          this.codeInputTargets[0].focus()
        }
      })

      this.modal.show()
    } else {
      console.error('showModal - No modal target found')
    }
  }


  // Handle TOTP input
  handleTOTPInput(event, index) {
    const value = event.target.value

    if (value.length === 1) {
      // Move to next input
      if (index < 5) {
        const nextInput = this.codeInputTargets[index + 1]
        if (nextInput) {
          nextInput.focus()
        }
      }
    } else if (value.length === 0) {
      // Move to previous input on backspace
      if (index > 0) {
        const prevInput = this.codeInputTargets[index - 1]
        if (prevInput) {
          prevInput.focus()
        }
      }
    }
  }

  // Handle TOTP keydown
  handleTOTPKeydown(event, index) {
    if (event.key === 'Backspace' && event.target.value === '') {
      // Move to previous input on backspace if current input is empty
      if (index > 0) {
        const prevInput = this.codeInputTargets[index - 1]
        if (prevInput) {
          prevInput.focus()
        }
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      // Move to previous input on left arrow
      const prevInput = this.codeInputTargets[index - 1]
      if (prevInput) {
        prevInput.focus()
      }
    } else if (event.key === 'ArrowRight' && index < 5) {
      // Move to next input on right arrow
      const nextInput = this.codeInputTargets[index + 1]
      if (nextInput) {
        nextInput.focus()
      }
    }
  }

  // Handle TOTP paste
  handleTOTPPaste(event) {
    event.preventDefault()
    const pastedData = (event.clipboardData || window.clipboardData).getData('text')
    const digits = pastedData.replace(/\D/g, '').slice(0, 6)
    if (digits.length === 6) {
      digits.split('').forEach((digit, index) => {
        if (this.codeInputTargets[index]) {
          this.codeInputTargets[index].value = digit
        }
      })

      const lastInput = this.codeInputTargets[5]
      if (lastInput) {
        lastInput.focus()
      }
    }
  }

  // Get form data
  getFormData() {
    const form = this.formTarget
    if (!form) return {}

    const formData = new FormData(form)
    const admin = {}
    const role_ids = []
    let start_admin_email = null

    for (let [key, value] of formData.entries()) {
      if (key === 'admin[email]') {
        admin.email = value
      } else if (key === 'admin[role_ids][]') {
        role_ids.push(value)
      } else if (key === 'start_admin_email') {
        start_admin_email = value
      }
    }

    if (role_ids.length > 0) {
      admin.role_ids = role_ids
    }

    const result = {admin}

    // Add start_admin_email if present
    if (start_admin_email) {
      result.start_admin_email = start_admin_email
    }

    return result
  }

  // Submit with TOTP verification
  async submitWithTOTP(event) {
    event.preventDefault()

    // Get TOTP code
    let totpCode = ''
    this.codeInputTargets.forEach(input => {
      totpCode += input.value
    })

    if (totpCode.length !== 6) {
      alert('Please enter a 6-digit verification code')
      return
    }

    // Show loading state
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.textContent = 'Verifying...'
    }

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')

    try {
      let response
      if (this.formData instanceof FormData) {
        // Send as multipart/form-data to preserve file uploads
        this.formData.append('totp_code', totpCode)
        response = await fetch(this.actionUrlValue, {
          method: this.actionMethodValue,
          headers: {
            'X-CSRF-Token': csrfToken,
            'Accept': 'application/json'
          },
          body: this.formData
        })
      } else {
        const submitData = {...this.formData, totp_code: totpCode}
        response = await fetch(this.actionUrlValue, {
          method: this.actionMethodValue,
          headers: {
            'X-CSRF-Token': csrfToken,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(submitData)
        })
      }

      // if (!response.ok) {
      //   throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      // }

      // Check if response is JSON
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json()

        if (result.success) {
          // Close modal
          this.closeModal()

          // Handle different action types
          if (this.actionMethodValue === 'DELETE') {
            // Dispatch delete success event
            const deleteEvent = new CustomEvent('deleteSuccess', {
              detail: {
                rowToDelete: this.rowToDelete,
                dataTable: this.dataTable,
                redirectUrl: this.redirectUrl
              }
            })
            document.dispatchEvent(deleteEvent)
          } else {
            // Show success message and redirect
            if (result.redirect_url) {
              window.location.href = result.redirect_url
            } else {
              window.location.reload()
            }
          }
        } else {
          if (result.redirect_url) {
            window.location.href = result.redirect_url
            return
          }

          // Show error
          alert(result.error || 'Verification failed. Please try again.')

          // Reset form
          this.codeInputTargets.forEach(input => {
            input.value = ''
          })
          this.codeInputTargets[0].focus()
        }
      } else {
        // Handle non-JSON response (e.g., HTML redirect)
        // Close modal first
        this.closeModal()

        if (this.actionMethodValue === 'DELETE') {
          // Dispatch delete success event
          const deleteEvent = new CustomEvent('deleteSuccess', {
            detail: {
              rowToDelete: this.rowToDelete,
              dataTable: this.dataTable,
              redirectUrl: this.redirectUrl
            }
          })
          document.dispatchEvent(deleteEvent)
        } else {
          // Reload page for HTML responses
          window.location.reload()
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      alert('An error occurred. Please try again.')
    } finally {
      // Reset button state
      if (this.hasSubmitBtnTarget) {
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.textContent = 'Verify'
      }
    }
  }

  // Cancel verification
  cancelVerification() {
    this.closeModal()
  }

  // Close modal
  closeModal() {
    if (this.modal) {
      this.modal.hide()
    }
  }

  // Modal cleanup
  cleanupModal() {
    if (this.modal) {
      this.modal.hide()
    }
  }

  // Modal shown event
  modalShown() {
    this.initializeTOTPInputs()
  }

  // Initialize TOTP inputs after modal is shown
  initializeTOTPInputs() {
    this.codeInputTargets.forEach((input, index) => {
      input.setAttribute('data-index', index)
      input.addEventListener('input', (e) => this.handleTOTPInput(e, index))
      input.addEventListener('keydown', (e) => this.handleTOTPKeydown(e, index))
    })

    if (this.hasTotpInputsTarget) {
      this.totpInputsTarget.addEventListener('paste', (e) => this.handleTOTPPaste(e))
    }
  }
} 